<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import Icon from '../icon/Icon.vue';

const props = defineProps({
  hasAssistants: {
    type: Boolean,
    default: false,
  },
  inboxUnbound: {
    type: Boolean,
    default: false,
  },
  quotaExhausted: {
    type: Boolean,
    default: false,
  },
  assistantId: {
    type: [Number, String],
    default: null,
  },
  threads: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['useSuggestion', 'selectThread']);
const { t } = useI18n();
const route = useRoute();

const conversationPrompts = [
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.SUMMARIZE.CONTENT',
  },
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.SUGGEST.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.SUGGEST.CONTENT',
  },
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.RATE.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.RATE.CONTENT',
  },
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.PAST_CONVERSATIONS.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.PAST_CONVERSATIONS.CONTENT',
  },
];

const dashboardPrompts = [
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.HIGH_PRIORITY.CONTENT',
  },
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.LIST_CONTACTS.CONTENT',
  },
  {
    label: 'CAPTAIN.COPILOT.PROMPTS.PAST_CONVERSATIONS.LABEL',
    prompt: 'CAPTAIN.COPILOT.PROMPTS.PAST_CONVERSATIONS.CONTENT',
  },
];

const getCurrentRoute = () => {
  const path = route.path;
  if (path.includes('/conversations')) return 'conversations';
  if (path.includes('/dashboard')) return 'dashboard';
  return 'dashboard';
};

const promptOptions = computed(() => {
  return getCurrentRoute() === 'conversations'
    ? conversationPrompts
    : dashboardPrompts;
});

const showPrompts = computed(
  () => props.hasAssistants && !props.inboxUnbound && !props.quotaExhausted
);

const handleSuggestion = opt => {
  emit('useSuggestion', t(opt.prompt));
};

const handleSelectThread = thread => {
  emit('selectThread', thread);
};
</script>

<template>
  <div class="flex-1 flex flex-col gap-6 px-2">
    <div class="flex flex-col space-y-4 py-4">
      <Icon icon="i-woot-captain" class="text-n-slate-9 text-4xl" />
      <div class="space-y-1">
        <h3 class="text-base font-medium text-n-slate-12 leading-8">
          {{ $t('CAPTAIN.COPILOT.PANEL_TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 leading-6">
          {{ $t('CAPTAIN.COPILOT.KICK_OFF_MESSAGE') }}
        </p>
      </div>
    </div>
    <div v-if="!hasAssistants" class="w-full space-y-2">
      <p class="text-sm text-n-slate-11 leading-6">
        {{ $t('CAPTAIN.ASSISTANTS.NO_ASSISTANTS_AVAILABLE') }}
      </p>
      <router-link
        :to="{
          name: 'captain_assistants_create_index',
          params: {
            accountId: route.params.accountId,
          },
        }"
        class="text-n-slate-11 underline hover:text-n-slate-12"
      >
        {{ $t('CAPTAIN.ASSISTANTS.ADD_NEW') }}
      </router-link>
    </div>
    <div v-else-if="quotaExhausted" class="w-full space-y-2">
      <p class="text-sm text-n-slate-11 leading-6">
        {{ $t('CAPTAIN.COPILOT.QUOTA_EXHAUSTED') }}
      </p>
      <router-link
        :to="{
          name: 'billing_settings_index',
          params: {
            accountId: route.params.accountId,
          },
        }"
        class="text-n-slate-11 underline hover:text-n-slate-12"
      >
        {{ $t('CAPTAIN.COPILOT.QUOTA_LINK') }}
      </router-link>
    </div>
    <div v-else-if="inboxUnbound" class="w-full space-y-2">
      <p class="text-sm text-n-slate-11 leading-6">
        {{ $t('CAPTAIN.COPILOT.INBOX_NOT_CONNECTED') }}
      </p>
      <router-link
        v-if="assistantId"
        :to="{
          name: 'captain_assistants_inboxes_index',
          params: {
            accountId: route.params.accountId,
            assistantId,
          },
        }"
        class="text-n-slate-11 underline hover:text-n-slate-12"
      >
        {{ $t('CAPTAIN.COPILOT.CONNECT_INBOX') }}
      </router-link>
    </div>
    <div v-else-if="showPrompts" class="w-full space-y-2">
      <span class="text-xs text-n-slate-10 block">
        {{ $t('CAPTAIN.COPILOT.TRY_THESE_PROMPTS') }}
      </span>
      <div class="space-y-1">
        <button
          v-for="prompt in promptOptions"
          :key="prompt.label"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 text-n-slate-11 flex items-center justify-between hover:bg-n-slate-3 transition-colors"
          @click="handleSuggestion(prompt)"
        >
          <span>{{ t(prompt.label) }}</span>
          <Icon icon="i-lucide-chevron-right" />
        </button>
      </div>
    </div>
    <div v-if="threads.length" class="w-full space-y-2">
      <span class="text-xs text-n-slate-10 block">
        {{ $t('CAPTAIN.COPILOT.PREVIOUS_CHATS') }}
      </span>
      <div class="space-y-1">
        <button
          v-for="thread in threads"
          :key="thread.id"
          class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 text-n-slate-11 text-left hover:bg-n-slate-3 transition-colors"
          @click="handleSelectThread(thread)"
        >
          <span class="line-clamp-2">{{ thread.title }}</span>
        </button>
      </div>
    </div>
  </div>
</template>
